"use client";

import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { HttpStatusCode } from "axios";
import { MediaService } from "../../auth/services/auth-service";
import CookieUtils from "../../../utils/cookie-utils";
import ImageCustom from "../../../components/custom/image-custom";
import { showToast } from "../../../components/custom/custom-toast";
import { useStoreOptions } from "../../../hooks/use-store-options";
import SelectBranchMultiDropdown from "../../../components/custom/select-branch-multi-dropdown";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import { ATTENDANCE_MODE, ATTENDANCE_MODE_OPTIONS } from "../../../constants/status";

export interface UserFormValues {
  username: string;
  name: string;
  email: string;
  phone: string;
  department_id: number | null;
  privilege_group_id: number | null;
  address: string;
  gender: number;
  joining_date: string | null;
  birthday: string | null;
  password: string;
  isActive: boolean;
  avatar?: string;
  id_card_number?: string;
  branch_ids?: number[];
  /** Ghi đè chế độ chấm công của bộ phận riêng cho NV này. NULL = kế thừa bộ phận. */
  attendance_mode: ATTENDANCE_MODE | null;
}

/** Dùng làm value cho option "Theo bộ phận" trong <select> — HTML select không có value null thật. */
const INHERIT_DEPARTMENT_VALUE = "inherit";

// DateInputCustom hiển thị dd/MM/yyyy; form + BE dùng YYYY-MM-DD → convert ở ranh giới component.
const toDMY = (ymd: string) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};

/**
 * SĐT di động VN: 10 số, bắt đầu 0 + đầu số 3/5/7/8/9 (vd 0901234567).
 * BE không validate định dạng nên đây là chốt chặn duy nhất.
 */
const PHONE_REGEX = /^0[35789]\d{8}$/;

/** Bỏ ký tự trang trí và quy +84/84 về 0 để người dùng dán số từ nguồn khác vẫn dùng được. */
const normalizePhone = (raw: string): string => {
  let v = (raw || "").replace(/[^\d+]/g, "");
  if (v.startsWith("+84")) v = "0" + v.slice(3);
  else if (v.startsWith("84") && v.length > 9) v = "0" + v.slice(2);
  return v.replace(/\D/g, "").slice(0, 10);
};

/** Chuỗi lỗi cho SĐT (rỗng = hợp lệ). `partial` = đang gõ → chưa báo lỗi khi số còn ngắn. */
const getPhoneError = (phone: string, partial = false): string => {
  const v = (phone || "").trim();
  if (!v) return partial ? "" : "Vui lòng nhập số điện thoại";
  if (!/^0/.test(v)) return "Số điện thoại phải bắt đầu bằng 0";
  if (v.length > 1 && !/^0[35789]/.test(v)) return "Đầu số không hợp lệ (03, 05, 07, 08, 09)";
  if (partial && v.length < 10) return "";
  if (!PHONE_REGEX.test(v)) return "Số điện thoại phải có đúng 10 chữ số";
  return "";
};

interface Option {
  label: string;
  value: number;
  status?: number;
}
interface DepartmentOption {
  value: number;
  label: string;
  status?: number;
  is_driver?: number | boolean;
}
interface UserCreateModalProps {
  onClose: () => void;
  onSubmit: (values: UserFormValues) => Promise<boolean>;
  isSubmitting: boolean;
  titleText?: string;
  submitText?: string;
  departmentOptions: DepartmentOption[];
  privilegeOptions: Option[];
  initialValues?: UserFormValues;
  mode?: "create" | "edit";
}

const defaultValues: UserFormValues = {
  username: "",
  name: "",
  email: "",
  phone: "",
  department_id: null,
  privilege_group_id: null,
  address: "",
  gender: 1,
  joining_date: "",
  birthday: "",
  password: "",
  isActive: true,
  avatar: undefined,
  id_card_number: "",
  branch_ids: [],
  attendance_mode: null,
};

const UserCreateModal: React.FC<UserCreateModalProps> = ({
  onClose,
  onSubmit,
  isSubmitting,
  titleText = "Thêm mới nhân viên",
  submitText = "Thêm mới",
  departmentOptions,
  privilegeOptions,
  initialValues,
  mode = "create",
}) => {
  const [form, setForm] = useState<UserFormValues>({ ...defaultValues, ...(initialValues ?? {}) });
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>();
  const [avatarFiles, setAvatarFiles] = useState<FileList | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pendingForm, setPendingForm] = useState<UserFormValues | null>(null);
  const [isAvatarLocal, setIsAvatarLocal] = useState<boolean>(false);
  const { uploadFiles: uploadAvatar, baseResponse: uploadResponse, isUploading } = MediaService.Upload();
  const { options: storeOptions = [] } = useStoreOptions();
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);

  const isEditMode = mode === "edit";
  /** === INIT FORM === */
  useEffect(() => {
    // Khởi tạo lại form
    setForm({ ...defaultValues, ...(initialValues ?? {}) });
    setErrors({});
  
    // Nếu có branch_ids → gán lại
    if (initialValues?.branch_ids?.length) {
      setSelectedBranches(initialValues.branch_ids);
    }
  
    // Chỉ set avatarPreview nếu chưa có ảnh local (người dùng chưa chọn ảnh mới)
    if (!isAvatarLocal) {
      if (initialValues?.avatar) {
        const avatarUrl = initialValues.avatar.startsWith("http")
          ? initialValues.avatar
          : `${CookieUtils.getResourceUrl() || ''}/${initialValues.avatar}`;
        setAvatarPreview(avatarUrl);
        setIsAvatarLocal(false);
      } else {
        setAvatarPreview(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);
  
  /** === OPTION FILTERS === */
  // Hiển thị tất cả bộ phận đang active (cả bộ phận tài xế lẫn không phải tài xế)
  // để admin có thể gán nhân viên vào bất kỳ bộ phận nào.
  const effectiveDepartmentOptions = useMemo(() => {
    const options = departmentOptions ?? [];

    const activeOptions = options.filter((op) => {
      const isActive = op.status === undefined || Number(op.status) === 1;
      return isActive;
    });

    if (
      isEditMode &&
      form.department_id &&
      activeOptions.every((op) => op.value !== form.department_id)
    ) {
      const current = options.find((op) => op.value === form.department_id);
      if (current) return [current, ...activeOptions];
    }

    return activeOptions;
  }, [departmentOptions, form.department_id, isEditMode]);
  
  const effectivePrivilegeOptions = useMemo(() => privilegeOptions ?? [], [privilegeOptions]);

  useEffect(() => {
    if (!form.privilege_group_id && effectivePrivilegeOptions.length > 0) {
      setForm((prev) => ({ ...prev, privilege_group_id: effectivePrivilegeOptions[0].value }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePrivilegeOptions]);
  useEffect(() => {
    if (initialValues?.branch_ids) {
      setSelectedBranches(
        initialValues.branch_ids.includes(-1)
          ? [-1]
          : initialValues.branch_ids
      );
    }
  }, [initialValues]);
  const combinedBranchOptions = useMemo(
    () => [{ value: 0, label: "Tất cả chi nhánh" }, ...storeOptions],
    [storeOptions]
  );

  // Note: selectedBranchValues available for future use with SelectDropdownAsyncPaginate
  const _selectedBranchValues = useMemo(() => {
    if (!selectedBranches.length) return [];
    if (selectedBranches.includes(0)) return [combinedBranchOptions[0]];
    return combinedBranchOptions.filter((op) => selectedBranches.includes(op.value));
  }, [selectedBranches, combinedBranchOptions]);
  void _selectedBranchValues; // Suppress unused warning

  /** === HANDLERS === */
  const handleChange = <K extends keyof UserFormValues>(key: K, value: UserFormValues[K]) => {
    if ((key === "joining_date" || key === "birthday") && value === "") {
      setForm((prev) => ({ ...prev, [key]: null as any }));
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * SĐT: chặn ký tự không phải số ngay khi gõ + báo lỗi trực tiếp (trước đây gõ gì cũng được,
   * tới lúc submit mới biết sai). Dùng `partial` để không nhấp nháy lỗi "phải đủ 10 số" khi mới gõ vài số.
   */
  const handlePhoneChange = (raw: string) => {
    const value = normalizePhone(raw);
    setForm((prev) => ({ ...prev, phone: value }));
    setErrors((prev) => ({ ...prev, phone: getPhoneError(value, true) }));
  };

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const file = files[0];
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      showToast({ message: "Định dạng ảnh không hỗ trợ. Vui lòng chọn JPG/PNG.", type: "warning" });
      return;
    }

    setAvatarPreview(URL.createObjectURL(file));
    setAvatarFiles(files);
    setIsAvatarLocal(true);
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = "Vui lòng nhập tên nhân viên";
    if (!form.username.trim()) nextErrors.username = "Vui lòng nhập tên đăng nhập";
    const phoneError = getPhoneError(form.phone);
    if (phoneError) nextErrors.phone = phoneError;
    if (!form.department_id) nextErrors.department_id = "Vui lòng chọn bộ phận";
    if (!selectedBranches.length) nextErrors.branch_ids = "Vui lòng chọn ít nhất 1 chi nhánh";
    if (!form.joining_date) nextErrors.joining_date = "Vui lòng chọn ngày vào làm của nhân viên";
    if (!form.birthday) nextErrors.birthday = "Vui lòng chọn ngày sinh của nhân viên";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  /** === SUBMIT === */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;
     let branchIdsToSend = selectedBranches.includes(-1)
     ? [-1]
     : selectedBranches;     
     const finalForm = { ...form, branch_ids: branchIdsToSend };
    // Nếu có file avatar → upload trước
    if (avatarFiles?.length) {
      setPendingForm(finalForm);
      await uploadAvatar(avatarFiles);
      return;
    }

    // Không có file → submit luôn
    const payload = { ...finalForm };
    if (!payload.avatar) delete payload.avatar;

    const success = await onSubmit(payload);
    if (success) onClose();
  };

  /** === UPLOAD CALLBACK === */
  useEffect(() => {
    if (!uploadResponse) return;
    if (uploadResponse.status !== HttpStatusCode.Ok) {
      showToast({ message: "Tải ảnh thất bại. Vui lòng thử lại.", type: "warning" });
      return;
    }

    let avatarPath: string | undefined;
    const data: any = uploadResponse.data;

    if (Array.isArray(data)) avatarPath = data[0]?.path ?? data[0]?.url ?? data[0]?.location;
    else if (data && typeof data === "object") avatarPath = data.path ?? data.url ?? data.location;

    if (!avatarPath) {
      showToast({ message: "Không lấy được đường dẫn ảnh sau khi tải lên.", type: "warning" });
      return;
    }

    const dataToSubmit = { ...(pendingForm ?? form), avatar: avatarPath, branch_ids: selectedBranches };
    (async () => {
      const ok = await onSubmit(dataToSubmit);
      if (ok) onClose();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadResponse]);

  /** === RENDER === */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="w-full max-w-4xl rounded-3xl bg-white p-8 shadow-2xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-semibold text-slate-800">{titleText}</h3>
          <button onClick={onClose} type="button" className="text-slate-400 hover:text-slate-600 text-xl">
            ✕
          </button>
        </div>

        <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
          {/* Avatar */}
          <div className="flex flex-col items-center gap-4">
            <div className="h-24 w-24 overflow-hidden rounded-full border-2 border-dashed border-blue-300 bg-blue-50 flex items-center justify-center">
              {avatarPreview ? (
                isAvatarLocal || mode === "create" ? (
                  <img src={avatarPreview} alt="avatar preview" className="h-full w-full object-cover" />
                ) : (
                  <ImageCustom url={avatarPreview} className="h-full w-full object-cover" />
                )
              ) : (
                <span className="text-2xl">🧑</span>
              )}
            </div>
            <label className="cursor-pointer rounded-full bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-600">
              Tải hình đại diện
              <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={handleAvatarChange} />
            </label>
          </div>

          {/* Fields */}
          <div className="grid gap-5 md:grid-cols-2">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Tên nhân viên <span className="text-red-500">*</span>
              </label>
              <input value={form.name} onChange={(e) => handleChange("name", e.target.value)} className="w-full rounded-xl border px-3 py-2" />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Tên đăng nhập <span className="text-red-500">*</span>
              </label>
              <input value={form.username} onChange={(e) => handleChange("username", e.target.value)} className="w-full rounded-xl border px-3 py-2" />
              {errors.username && <p className="mt-1 text-xs text-red-500">{errors.username}</p>}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-slate-700">Giới tính</label>
              <div className="flex items-center gap-6 rounded-xl border px-3 py-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="gender" checked={form.gender === 1} onChange={() => handleChange("gender", 1)} /> Nam
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="gender" checked={form.gender === 0} onChange={() => handleChange("gender", 0)} /> Nữ
                </label>
              </div>
            </div>

            {/* Dates */}
            <div>
              <label className="block text-sm font-medium text-slate-700">Ngày làm việc<span className="text-red-500">*</span></label>
              <DateInputCustom
                type="date"
                placeholder="dd/mm/yyyy"
                value={toDMY(form.joining_date ?? "")}
                onChange={(d) => handleChange("joining_date", toYMD(d ?? ""))}
                isShowClearDate
                className="w-full"
              />
              {errors.joining_date && <p className="mt-1 text-xs text-red-500">{errors.joining_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Ngày sinh<span className="text-red-500">*</span></label>
              <DateInputCustom
                type="date"
                placeholder="dd/mm/yyyy"
                value={toDMY(form.birthday ?? "")}
                onChange={(d) => handleChange("birthday", toYMD(d ?? ""))}
                // Ngày sinh không thể ở tương lai.
                maxDate={toDMY(new Date().toISOString().slice(0, 10))}
                isShowClearDate
                className="w-full"
              />
              {errors.birthday && <p className="mt-1 text-xs text-red-500">{errors.birthday}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Số điện thoại <span className="text-red-500">*</span>
              </label>
              <input
                value={form.phone}
                inputMode="numeric"
                maxLength={10}
                placeholder="VD: 0901234567"
                onChange={(e) => handlePhoneChange(e.target.value)}
                // Rời ô: kiểm tra đầy đủ (kể cả khi số còn thiếu chữ số) để báo ngay, không đợi submit.
                onBlur={() => setErrors((prev) => ({ ...prev, phone: getPhoneError(form.phone) }))}
                className={`w-full rounded-xl border px-3 py-2 ${errors.phone ? "border-red-400" : ""}`}
              />
              {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700">Email</label>
              <input type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className="w-full rounded-xl border px-3 py-2" />
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Bộ phận <span className="text-red-500">*</span>
              </label>
              <select value={form.department_id ?? ""} onChange={(e) => handleChange("department_id", e.target.value ? Number(e.target.value) : null)} className="w-full rounded-xl border px-3 py-2">
                <option value="">Chọn bộ phận</option>
                {effectiveDepartmentOptions.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
              {errors.department_id && <p className="mt-1 text-xs text-red-500">{errors.department_id}</p>}
            </div>

            {/* Attendance mode override */}
            <div>
              <label className="block text-sm font-medium text-slate-700">Chế độ chấm công riêng</label>
              <select
                value={form.attendance_mode ?? INHERIT_DEPARTMENT_VALUE}
                onChange={(e) =>
                  handleChange(
                    "attendance_mode",
                    e.target.value === INHERIT_DEPARTMENT_VALUE ? null : (Number(e.target.value) as ATTENDANCE_MODE)
                  )
                }
                className="w-full rounded-xl border px-3 py-2"
              >
                <option value={INHERIT_DEPARTMENT_VALUE}>Theo bộ phận (mặc định)</option>
                {ATTENDANCE_MODE_OPTIONS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                Chỉ chọn khi NV này cần chế độ khác với bộ phận (vd: quản lý bộ phận theo ca nhưng bản thân không xếp ca).
              </p>
            </div>
            {/* CCCD */}
            <div>
              <label className="block text-sm font-medium text-slate-700">CCCD</label>
              <input value={form.id_card_number ?? ""} onChange={(e) => handleChange("id_card_number", e.target.value)} className="w-full rounded-xl border px-3 py-2" placeholder="Nhập CCCD" />
            </div>

            {/* Address */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700">Địa chỉ</label>
              <textarea value={form.address} onChange={(e) => handleChange("address", e.target.value)} className="w-full rounded-xl border px-3 py-2" rows={2} placeholder="Nhập địa chỉ" />
            </div>
          </div>

            {/* === Branch Multi-select (SelectDropdownAsyncPaginate dùng lại component cũ) === */}
              <div className="mt-4">
              <SelectBranchMultiDropdown
              selected={selectedBranches}
              onChange={(values) => setSelectedBranches(values.includes(-1) ? [-1] : values)}
              error={errors.branch_ids}
            />
            </div>
          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="rounded-full border px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
              Hủy
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isUploading}
              className="rounded-full bg-green-600 px-6 py-2 text-sm font-semibold text-white shadow hover:bg-green-700 disabled:opacity-60"
            >
              {isSubmitting || isUploading ? "Đang lưu..." : submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCreateModal;
